// @ts-ignore
import { gaApiCall, type ApiResult } from "@utils/httpClient";
// @ts-ignore
import { hasProp, assert } from "@utils/assertion";
// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { renderJsonWithNumbersAsBigint, parseJsonWithNumbersAsBigint, type ParserResult } from "@utils/json";
import * as schema from "@newgenerated/shared/schema";
import * as mapper from "@newgenerated/shared/mapper";
export async function trackOutboundLinkClick(props: { body: schema.OutboundLink }): Promise<ApiResult> {
  const path = "/gaapi/act/olc";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_OutboundLink(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function trackPageView(props: { body: schema.PageViewPayload }): Promise<ApiResult> {
  const path = "/gaapi/act/pv";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_PageViewPayload(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function trackShareEvent(props: { body: schema.ShareEvent }): Promise<ApiResult> {
  const path = "/gaapi/analytics/track-share-event";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ShareEvent(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getBulkLoadActionables(props: { body: schema.ActionableBulkLoadForm }): Promise<ApiResult<schema.ActionableMetadataArray>> {
  const path = "/gaapi/actionables-bulk-load/admin";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ActionableBulkLoadForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ActionableMetadataArray> => {
    return mapper.deserialize_ActionableMetadataArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getActionableOverview(): Promise<ApiResult<schema.ActionableMetadataArray>> {
  const path = "/gaapi/actionables/admin";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ActionableMetadataArray> => {
    return mapper.deserialize_ActionableMetadataArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function searchActionables(props: { body: schema.ActionableAdminSearchForm; pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedActionableMetadata>> {
  let path = "/gaapi/actionables/admin-search";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ActionableAdminSearchForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedActionableMetadata> => {
    return mapper.deserialize_PagedActionableMetadata(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function toggleLike(props: { feedbackId: bigint }): Promise<ApiResult> {
  let path = "/gaapi/actionables/peerinsight/change-like";
  const urlSearchParams = new URLSearchParams();
  [props.feedbackId].flat().forEach((value) => {
    urlSearchParams.append(
      "feedbackId",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function navigateForm(props: { body: schema.PeerInsightFormNavigation }): Promise<ApiResult<bigint>> {
  const path = "/gaapi/actionables/peerinsight/navigateForm";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_PeerInsightFormNavigation(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<bigint> => {
    const data = parseJsonWithNumbersAsBigint(value);
    return ((value) => {
      if (typeof value === "number") {
        return { success: true, value: BigInt(value) };
      }
      if (typeof value === "bigint") {
        return { success: true, value: value };
      }
      return {
        success: false,
        reason: `[class IntegerSchema {
    class Schema {
        type: integer
        format: int64
        $ref: null
        description: null
        title: null
        multipleOf: null
        maximum: null
        exclusiveMaximum: null
        minimum: null
        exclusiveMinimum: null
        maxLength: null
        minLength: null
        pattern: null
        maxItems: null
        minItems: null
        uniqueItems: null
        maxProperties: null
        minProperties: null
        required: null
        not: null
        properties: null
        additionalProperties: null
        nullable: true
        readOnly: null
        writeOnly: null
        example: null
        externalDocs: null
        deprecated: null
        discriminator: null
        xml: null
    }
}]: '${value}' expected to be of type bigint or number.`,
      };
    })(data);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function deleteById(props: { feedbackId: bigint }): Promise<ApiResult> {
  let path = "/gaapi/actionables/peerinsight/delete-feedback";
  const urlSearchParams = new URLSearchParams();
  [props.feedbackId].flat().forEach((value) => {
    urlSearchParams.append(
      "feedbackId",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getQuestionKeys(): Promise<ApiResult<schema.PeerInsightQuestionKeysResponse>> {
  const path = "/gaapi/actionables/peerinsight/question-keys";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PeerInsightQuestionKeysResponse> => {
    return mapper.deserialize_PeerInsightQuestionKeysResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getTextFeedbacks(props: { body: schema.PeerInsightTextFeedbackRequest }): Promise<ApiResult<schema.PagedPeerInsightFeedbackListView>> {
  const path = "/gaapi/actionables/peerinsight/text-feedback";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_PeerInsightTextFeedbackRequest(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedPeerInsightFeedbackListView> => {
    return mapper.deserialize_PagedPeerInsightFeedbackListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function updateApprovalStatus(props: { body: schema.PeerInsightFeedbackUpdateRequest }): Promise<ApiResult> {
  const path = "/gaapi/actionables/peerinsight/update-feedback";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_PeerInsightFeedbackUpdateRequest(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getActionableEditView(props: { actionableId: bigint }): Promise<ApiResult<schema.ActionableEditView>> {
  let path = "/gaapi/actionables/{actionableId}/admin";
  path = path.replace(
    "{actionableId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.actionableId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ActionableEditView> => {
    return mapper.deserialize_ActionableEditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveActionable(props: { body: schema.ActionableEditForm; actionableId: bigint }): Promise<ApiResult<schema.ActionableEditResult>> {
  let path = "/gaapi/actionables/{actionableId}/admin";
  path = path.replace(
    "{actionableId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.actionableId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ActionableEditForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ActionableEditResult> => {
    return mapper.deserialize_ActionableEditResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function toggleBookmarkActionable(props: { actionableId: bigint }): Promise<ApiResult<schema.BookmarkResponse>> {
  let path = "/gaapi/actionables/{actionableId}/bookmark";
  path = path.replace(
    "{actionableId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.actionableId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.BookmarkResponse> => {
    return mapper.deserialize_BookmarkResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function setExperienceFeedback(props: { actionableId: bigint; score: number }): Promise<ApiResult> {
  let path = "/gaapi/actionables/{actionableId}/experience-feedback";
  path = path.replace(
    "{actionableId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.actionableId),
  );
  const urlSearchParams = new URLSearchParams();
  [props.score].flat().forEach((value) => {
    urlSearchParams.append(
      "score",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function trackPeerInsightStarted(props: { actionableId: bigint }): Promise<ApiResult> {
  let path = "/gaapi/actionables/{actionableId}/peerinsight-started";
  path = path.replace(
    "{actionableId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.actionableId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function trackProTalkVideoProgress(props: { actionableId: bigint; progress: number }): Promise<ApiResult> {
  let path = "/gaapi/actionables/{actionableId}/pro-talk-video-progress";
  path = path.replace(
    "{actionableId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.actionableId),
  );
  const urlSearchParams = new URLSearchParams();
  [props.progress].flat().forEach((value) => {
    urlSearchParams.append(
      "progress",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function trackStep2VideoProgress(props: { actionableId: bigint; progress: number }): Promise<ApiResult> {
  let path = "/gaapi/actionables/{actionableId}/step2-video-progress";
  path = path.replace(
    "{actionableId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.actionableId),
  );
  const urlSearchParams = new URLSearchParams();
  [props.progress].flat().forEach((value) => {
    urlSearchParams.append(
      "progress",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getChannels(props: { includeInactive: boolean }): Promise<ApiResult<schema.ChannelList>> {
  let path = "/gaapi/admin/channels";
  const urlSearchParams = new URLSearchParams();
  [props.includeInactive].flat().forEach((value) => {
    urlSearchParams.append(
      "includeInactive",
      ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "boolean");
        return parsed_again ? "true" : "false";
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ChannelList> => {
    return mapper.deserialize_ChannelList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getAllAbstracts(props: { listType: string }): Promise<ApiResult<schema.AllAbstractsListWrapperArray>> {
  let path = "/gaapi/all-abstracts/{listType}";
  path = path.replace(
    "{listType}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.listType),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.AllAbstractsListWrapperArray> => {
    return mapper.deserialize_AllAbstractsListWrapperArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getAuthorById(props: { query: bigint }): Promise<ApiResult<schema.AuthorListView>> {
  let path = "/gaapi/authors/admin/getAuthorById";
  const urlSearchParams = new URLSearchParams();
  [props.query].flat().forEach((value) => {
    urlSearchParams.append(
      "query",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.AuthorListView> => {
    return mapper.deserialize_AuthorListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function findAuthorsByName(props: { query: string }): Promise<ApiResult<schema.AuthorListViewList>> {
  let path = "/gaapi/authors/findAuthorsByName";
  const urlSearchParams = new URLSearchParams();
  [props.query].flat().forEach((value) => {
    urlSearchParams.append(
      "query",
      ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.AuthorListViewList> => {
    return mapper.deserialize_AuthorListViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCareerOverview(): Promise<ApiResult<schema.CareerListViewList>> {
  const path = "/gaapi/careers/admin";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CareerListViewList> => {
    return mapper.deserialize_CareerListViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCareerEditView(props: { careerId: bigint }): Promise<ApiResult<schema.CareerEditView>> {
  let path = "/gaapi/careers/{careerId}/admin";
  path = path.replace(
    "{careerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.careerId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CareerEditView> => {
    return mapper.deserialize_CareerEditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveCareer(props: { body: schema.CareerForm; careerId: bigint }): Promise<ApiResult<schema.CareerEditView>> {
  let path = "/gaapi/careers/{careerId}/admin";
  path = path.replace(
    "{careerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.careerId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CareerForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CareerEditView> => {
    return mapper.deserialize_CareerEditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function searchAdminChannels(props: { form: schema.ChannelAdminSearchForm; page: number; psize: number }): Promise<ApiResult<schema.ChannelMetadataArray>> {
  let path = "/gaapi/channels/admin-search";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_ChannelAdminSearchForm(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.form);
    assert(hasProp(outerParsed, "corporateId"));
    [outerParsed.corporateId].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "corporateId",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "includeInactive"));
    [outerParsed.includeInactive].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "includeInactive",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "includeTemporary"));
    [outerParsed.includeTemporary].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "includeTemporary",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "query"));
    [outerParsed.query].flat().forEach((value) => {
      urlSearchParams.append(
        "query",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  [props.page].flat().forEach((value) => {
    urlSearchParams.append(
      "page",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  [props.psize].flat().forEach((value) => {
    urlSearchParams.append(
      "psize",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ChannelMetadataArray> => {
    return mapper.deserialize_ChannelMetadataArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getFeaturedChannels(): Promise<ApiResult<schema.ChannelMetadataArray>> {
  const path = "/gaapi/channels/featured/admin";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ChannelMetadataArray> => {
    return mapper.deserialize_ChannelMetadataArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function updateFeaturedChannels(props: { body: schema.ChannelMetadataArray }): Promise<ApiResult> {
  const path = "/gaapi/channels/featured/admin";
  const response = await gaApiCall({
    path: path,
    method: "PUT",
    requestBody: ((value) => {
      return mapper.serialize_ChannelMetadataArray(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function follow(props: { channelId: bigint }): Promise<ApiResult<schema.ChannelFollowerStatus>> {
  let path = "/gaapi/channels/{channelId}/follow";
  path = path.replace(
    "{channelId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.channelId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ChannelFollowerStatus> => {
    return mapper.deserialize_ChannelFollowerStatus(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function unfollow(props: { channelId: bigint }): Promise<ApiResult<schema.ChannelFollowerStatus>> {
  let path = "/gaapi/channels/{channelId}/follow";
  path = path.replace(
    "{channelId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.channelId),
  );
  const response = await gaApiCall({ path: path, method: "DELETE", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ChannelFollowerStatus> => {
    return mapper.deserialize_ChannelFollowerStatus(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getRelatedChannels(props: { channelId: bigint }): Promise<ApiResult<schema.ChannelList>> {
  let path = "/gaapi/channels/{channelId}/related";
  path = path.replace(
    "{channelId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.channelId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ChannelList> => {
    return mapper.deserialize_ChannelList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getAllLibraryConnectors(): Promise<ApiResult<schema.LibraryConnectorListViewArray>> {
  const path = "/gaapi/connectors";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.LibraryConnectorListViewArray> => {
    return mapper.deserialize_LibraryConnectorListViewArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getContent(props: { body: schema.ContentMetadataFilterForm }): Promise<ApiResult<schema.ContentMetadataView>> {
  const path = "/gaapi/content";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ContentMetadataFilterForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ContentMetadataView> => {
    return mapper.deserialize_ContentMetadataView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getIxStatus(props: { body: schema.ContentIdList }): Promise<ApiResult<schema.ContentIxStatusList>> {
  const path = "/gaapi/content-ix-status";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ContentIdList(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ContentIxStatusList> => {
    return mapper.deserialize_ContentIxStatusList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getContextFlags(): Promise<ApiResult<schema.ContextFlagArr>> {
  const path = "/gaapi/context-flags";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ContextFlagArr> => {
    return mapper.deserialize_ContextFlagArr(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function downloadLibraryExportExcel(props: { corporateId: number }): Promise<ApiResult<string>> {
  let path = "/gaapi/corporation/{corporateId}/library/export/download";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<string> => {
    const data = parseJsonWithNumbersAsBigint(value);
    return ((value) => {
      if (typeof value !== "string") {
        return {
          success: false,
          reason: `[class StringSchema {
    class Schema {
        type: string
        format: null
        $ref: null
        description: null
        title: null
        multipleOf: null
        maximum: null
        exclusiveMaximum: null
        minimum: null
        exclusiveMinimum: null
        maxLength: null
        minLength: null
        pattern: null
        maxItems: null
        minItems: null
        uniqueItems: null
        maxProperties: null
        minProperties: null
        required: null
        not: null
        properties: null
        additionalProperties: null
        nullable: true
        readOnly: null
        writeOnly: null
        example: null
        externalDocs: null
        deprecated: null
        discriminator: null
        xml: null
    }
}]: '${value}' expected to be of type string.`,
        };
      }
      return { success: true, value: value };
    })(data);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCorporations(): Promise<ApiResult<schema.CorporationListViewList>> {
  const path = "/gaapi/corporations";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CorporationListViewList> => {
    return mapper.deserialize_CorporationListViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCorporation(props: { corporateId: number }): Promise<ApiResult<schema.CorporationPortalAdminView>> {
  let path = "/gaapi/corporations/{corporateId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CorporationPortalAdminView> => {
    return mapper.deserialize_CorporationPortalAdminView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCorporationComments(props: { corporateId: number; pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedCorporateCommentListView>> {
  let path = "/gaapi/corporations/{corporateId}/comments";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedCorporateCommentListView> => {
    return mapper.deserialize_PagedCorporateCommentListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function createCorporateComment(props: { body: schema.CorporateCommentForm; corporateId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/comments";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CorporateCommentForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getCorporateCommentForm(props: { corporateId: number; corporateCommentId: number }): Promise<ApiResult<schema.CorporateCommentForm>> {
  let path = "/gaapi/corporations/{corporateId}/comments/{corporateCommentId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{corporateCommentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateCommentId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CorporateCommentForm> => {
    return mapper.deserialize_CorporateCommentForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function updateCorporateComment(props: { body: schema.CorporateCommentForm; corporateId: number; corporateCommentId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/comments/{corporateCommentId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{corporateCommentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateCommentId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CorporateCommentForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function deleteCorporateComment(props: { corporateId: number; corporateCommentId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/comments/{corporateCommentId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{corporateCommentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateCommentId),
  );
  const response = await gaApiCall({ path: path, method: "DELETE", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getPagedConnectorCompletionExecutionLogs(props: {
  corporateId: number;
  pageRequest: schema.PageRequest;
  connectorCompletionFilters: schema.ConnectorCompletionFilters;
}): Promise<ApiResult<schema.PagedConnectorCompletionExecutionLogListView>> {
  let path = "/gaapi/corporations/{corporateId}/connector-completion-execution-logs";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_ConnectorCompletionFilters(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.connectorCompletionFilters);
    assert(hasProp(outerParsed, "timeFrame"));
    [outerParsed.timeFrame].flat().forEach((value) => {
      urlSearchParams.append(
        "timeFrame",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "userId"));
    [outerParsed.userId].flat().forEach((value) => {
      urlSearchParams.append(
        "userId",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedConnectorCompletionExecutionLogListView> => {
    return mapper.deserialize_PagedConnectorCompletionExecutionLogListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function syncConnectorByCorporateIdAndConnectorId(props: { body: schema.ConnectorSyncForm; corporateId: number; connectorId: bigint }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/connectors/{connectorId}/sync";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{connectorId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.connectorId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ConnectorSyncForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getCorporateContactsByCorporateId(props: { corporateId: number; resource: schema.CorporateContactResource }): Promise<ApiResult<schema.CorporateContactListViewList>> {
  let path = "/gaapi/corporations/{corporateId}/contacts";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const urlSearchParams = new URLSearchParams();
  [props.resource].flat().forEach((value) => {
    urlSearchParams.append(
      "resource",
      ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return mapper.serialize_CorporateContactResource(value);
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CorporateContactListViewList> => {
    return mapper.deserialize_CorporateContactListViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function createCorporateContact(props: { body: schema.CorporateContactForm; corporateId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/contacts";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CorporateContactForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function updateCorporateContact(props: { body: schema.CorporateContactForm; corporateId: number; corporateContactId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/contacts/{corporateContactId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{corporateContactId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateContactId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CorporateContactForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function deleteCorporateContact(props: { corporateId: number; corporateContactId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/contacts/{corporateContactId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{corporateContactId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateContactId),
  );
  const response = await gaApiCall({ path: path, method: "DELETE", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getCustomPages(props: { corporateId: number; customPageAdminSearchForm: schema.CustomPageAdminSearchForm; pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedCustomPageEditListView>> {
  let path = "/gaapi/corporations/{corporateId}/custom-pages";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_CustomPageAdminSearchForm(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.customPageAdminSearchForm);
    assert(hasProp(outerParsed, "includeInactive"));
    [outerParsed.includeInactive].flat().forEach((value) => {
      urlSearchParams.append(
        "includeInactive",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "onlyHighlighted"));
    [outerParsed.onlyHighlighted].flat().forEach((value) => {
      urlSearchParams.append(
        "onlyHighlighted",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "searchTerm"));
    [outerParsed.searchTerm].flat().forEach((value) => {
      urlSearchParams.append(
        "searchTerm",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedCustomPageEditListView> => {
    return mapper.deserialize_PagedCustomPageEditListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCustomPage(props: { corporateId: number; customPageId: bigint }): Promise<ApiResult<schema.CustomPageEditView>> {
  let path = "/gaapi/corporations/{corporateId}/custom-pages/{customPageId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{customPageId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customPageId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomPageEditView> => {
    return mapper.deserialize_CustomPageEditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function archiveCustomPage(props: { corporateId: number; customPageId: bigint }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/custom-pages/{customPageId}/archive";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{customPageId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customPageId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function copyCustomPage(props: { body: schema.CustomPageCopyRequest; corporateId: number; customPageId: bigint }): Promise<ApiResult<string>> {
  let path = "/gaapi/corporations/{corporateId}/custom-pages/{customPageId}/copy";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{customPageId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customPageId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CustomPageCopyRequest(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<string> => {
    const data = parseJsonWithNumbersAsBigint(value);
    return ((value) => {
      if (typeof value !== "string") {
        return {
          success: false,
          reason: `[class StringSchema {
    class Schema {
        type: string
        format: null
        $ref: null
        description: null
        title: null
        multipleOf: null
        maximum: null
        exclusiveMaximum: null
        minimum: null
        exclusiveMinimum: null
        maxLength: null
        minLength: null
        pattern: null
        maxItems: null
        minItems: null
        uniqueItems: null
        maxProperties: null
        minProperties: null
        required: null
        not: null
        properties: null
        additionalProperties: null
        nullable: null
        readOnly: null
        writeOnly: null
        example: null
        externalDocs: null
        deprecated: null
        discriminator: null
        xml: null
    }
}]: '${value}' expected to be of type string.`,
        };
      }
      return { success: true, value: value };
    })(data);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function searchCustomPages(props: {
  corporateId: number;
  pageRequest: schema.PageRequest;
  customPageAdminSearchForm: schema.CustomPageAdminSearchForm;
  isoCode: schema.Language;
}): Promise<ApiResult<schema.PagedCustomPageListView>> {
  let path = "/gaapi/corporations/{corporateId}/custom-pages/admin-search";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_CustomPageAdminSearchForm(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.customPageAdminSearchForm);
    assert(hasProp(outerParsed, "includeInactive"));
    [outerParsed.includeInactive].flat().forEach((value) => {
      urlSearchParams.append(
        "includeInactive",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "onlyHighlighted"));
    [outerParsed.onlyHighlighted].flat().forEach((value) => {
      urlSearchParams.append(
        "onlyHighlighted",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "searchTerm"));
    [outerParsed.searchTerm].flat().forEach((value) => {
      urlSearchParams.append(
        "searchTerm",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  [props.isoCode].flat().forEach((value) => {
    urlSearchParams.append(
      "isoCode",
      ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return mapper.serialize_Language(value);
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedCustomPageListView> => {
    return mapper.deserialize_PagedCustomPageListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCustomPagesHighlighted(props: { corporateId: number }): Promise<ApiResult<schema.CustomPageEditListViewArray>> {
  let path = "/gaapi/corporations/{corporateId}/custom-pages/highlighted";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomPageEditListViewArray> => {
    return mapper.deserialize_CustomPageEditListViewArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveCustomPagesHighlighted(props: { body: schema.CustomPageHighlightsForm; corporateId: number }): Promise<ApiResult<schema.CustomPageEditListViewArray>> {
  let path = "/gaapi/corporations/{corporateId}/custom-pages/highlighted";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "PUT",
    requestBody: ((value) => {
      return mapper.serialize_CustomPageHighlightsForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomPageEditListViewArray> => {
    return mapper.deserialize_CustomPageEditListViewArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function massImportProcessing(props: { body: schema.CustomerMassImportForm; corporateId: number }): Promise<ApiResult<schema.CustomerMassImportProcessResponse>> {
  let path = "/gaapi/corporations/{corporateId}/customers/mass-import-processing";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CustomerMassImportForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerMassImportProcessResponse> => {
    return mapper.deserialize_CustomerMassImportProcessResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function massImportUpload(props: { body: schema.BytesData; corporateId: number }): Promise<ApiResult<schema.CustomerMassImportUploadResponse>> {
  let path = "/gaapi/corporations/{corporateId}/customers/mass-import-upload";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_BytesData(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerMassImportUploadResponse> => {
    return mapper.deserialize_CustomerMassImportUploadResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getPortletContainer(props: { corporateId: number; isoCode: schema.Language }): Promise<ApiResult<schema.PortletContainerAdminEditView>> {
  let path = "/gaapi/corporations/{corporateId}/portlet-containers/start-page/languages/{isoCode}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{isoCode}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return mapper.serialize_Language(value);
        })(),
      );
    })(props.isoCode),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PortletContainerAdminEditView> => {
    return mapper.deserialize_PortletContainerAdminEditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function savePortletContainer(props: { body: schema.PortletContainerAdminEditForm; corporateId: number; isoCode: schema.Language }): Promise<ApiResult<schema.PortletContainerAdminEditView>> {
  let path = "/gaapi/corporations/{corporateId}/portlet-containers/start-page/languages/{isoCode}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{isoCode}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return mapper.serialize_Language(value);
        })(),
      );
    })(props.isoCode),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_PortletContainerAdminEditForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PortletContainerAdminEditView> => {
    return mapper.deserialize_PortletContainerAdminEditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function deletePortletContainer(props: { corporateId: number; portletContainerId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/portlet-containers/{portletContainerId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{portletContainerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.portletContainerId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getMyListPortletView(props: { corporateId: number; portletId: number; isoCode: schema.Language }): Promise<ApiResult<schema.MyListPortletView>> {
  let path = "/gaapi/corporations/{corporateId}/portlets/mylist-{portletId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{portletId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.portletId),
  );
  const urlSearchParams = new URLSearchParams();
  [props.isoCode].flat().forEach((value) => {
    urlSearchParams.append(
      "isoCode",
      ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return mapper.serialize_Language(value);
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.MyListPortletView> => {
    return mapper.deserialize_MyListPortletView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getConfigEditView(props: { corporateId: number }): Promise<ApiResult<schema.PromoBoxConfigEditView>> {
  let path = "/gaapi/corporations/{corporateId}/promobox-config";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PromoBoxConfigEditView> => {
    return mapper.deserialize_PromoBoxConfigEditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveConfig(props: { body: schema.PromoBoxConfigForm; corporateId: number }): Promise<ApiResult<schema.PromoBoxConfigEditView>> {
  let path = "/gaapi/corporations/{corporateId}/promobox-config";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "PUT",
    requestBody: ((value) => {
      return mapper.serialize_PromoBoxConfigForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PromoBoxConfigEditView> => {
    return mapper.deserialize_PromoBoxConfigEditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getPromoBoxConfigPreview(props: { body: schema.PromoBoxConfigPreviewFilters; corporateId: number }): Promise<ApiResult<schema.PromoBoxConfigPreview>> {
  let path = "/gaapi/corporations/{corporateId}/promobox-config-preview";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_PromoBoxConfigPreviewFilters(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PromoBoxConfigPreview> => {
    return mapper.deserialize_PromoBoxConfigPreview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getPromoBoxLibrary(props: { corporateId: number }): Promise<ApiResult<schema.PromoBoxLibraryView>> {
  let path = "/gaapi/corporations/{corporateId}/promoboxes";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PromoBoxLibraryView> => {
    return mapper.deserialize_PromoBoxLibraryView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getPromoBox(props: { promoBoxId: number; corporateId: number }): Promise<ApiResult<schema.PromoBoxEditView>> {
  let path = "/gaapi/corporations/{corporateId}/promoboxes/{promoBoxId}";
  path = path.replace(
    "{promoBoxId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.promoBoxId),
  );
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PromoBoxEditView> => {
    return mapper.deserialize_PromoBoxEditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function savePromoBox(props: { body: schema.PromoBoxEditForm; promoBoxId: number; corporateId: number }): Promise<ApiResult<schema.PromoBoxEditView>> {
  let path = "/gaapi/corporations/{corporateId}/promoboxes/{promoBoxId}";
  path = path.replace(
    "{promoBoxId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.promoBoxId),
  );
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_PromoBoxEditForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PromoBoxEditView> => {
    return mapper.deserialize_PromoBoxEditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function deletePromoBoxById(props: { corporateId: number; promoBoxId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/promoboxes/{promoBoxId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{promoBoxId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.promoBoxId),
  );
  const response = await gaApiCall({ path: path, method: "DELETE", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getDomainProtectionForm(props: { corporateId: number }): Promise<ApiResult<schema.DomainProtectionForm>> {
  let path = "/gaapi/corporations/{corporateId}/protections/domain";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DomainProtectionForm> => {
    return mapper.deserialize_DomainProtectionForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveDomainProtectionForm(props: { body: schema.DomainProtectionForm; corporateId: number }): Promise<ApiResult<schema.DomainProtectionFormSaveResponse>> {
  let path = "/gaapi/corporations/{corporateId}/protections/domain";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_DomainProtectionForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DomainProtectionFormSaveResponse> => {
    return mapper.deserialize_DomainProtectionFormSaveResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getIpProtectionForm(props: { corporateId: number }): Promise<ApiResult<schema.IpProtectionForm>> {
  let path = "/gaapi/corporations/{corporateId}/protections/ip";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.IpProtectionForm> => {
    return mapper.deserialize_IpProtectionForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveIpProtectionForm(props: { body: schema.IpProtectionForm; corporateId: number }): Promise<ApiResult<schema.IpProtectionFormSaveResponse>> {
  let path = "/gaapi/corporations/{corporateId}/protections/ip";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_IpProtectionForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.IpProtectionFormSaveResponse> => {
    return mapper.deserialize_IpProtectionFormSaveResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getReferrerProtectionForm(props: { corporateId: number }): Promise<ApiResult<schema.ReferrerProtectionForm>> {
  let path = "/gaapi/corporations/{corporateId}/protections/referrer";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ReferrerProtectionForm> => {
    return mapper.deserialize_ReferrerProtectionForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveReferrerProtectionForm(props: { body: schema.ReferrerProtectionForm; corporateId: number }): Promise<ApiResult<schema.ReferrerProtectionFormSaveResponse>> {
  let path = "/gaapi/corporations/{corporateId}/protections/referrer";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ReferrerProtectionForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ReferrerProtectionFormSaveResponse> => {
    return mapper.deserialize_ReferrerProtectionFormSaveResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function deleteAllSamlLogs(props: { corporateId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/saml-logs";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "DELETE", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function searchSamlLogs(props: { corporateId: number }): Promise<ApiResult<schema.CorporationSamlLogListViewArray>> {
  let path = "/gaapi/corporations/{corporateId}/saml-logs/search";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CorporationSamlLogListViewArray> => {
    return mapper.deserialize_CorporationSamlLogListViewArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getAllActionableSensitiveContents(props: { corporateId: number }): Promise<ApiResult<schema.SensitiveContentListViewList>> {
  let path = "/gaapi/corporations/{corporateId}/sensitive-contents/actionables";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.SensitiveContentListViewList> => {
    return mapper.deserialize_SensitiveContentListViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveActionableSensitiveContents(props: { body: schema.ActionableIdsList; corporateId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/sensitive-contents/actionables";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ActionableIdsList(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getAllChannelSensitiveContents(props: { corporateId: number }): Promise<ApiResult<schema.SensitiveContentListViewList>> {
  let path = "/gaapi/corporations/{corporateId}/sensitive-contents/channels";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.SensitiveContentListViewList> => {
    return mapper.deserialize_SensitiveContentListViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveChannelSensitiveContents(props: { body: schema.ChannelIdsList; corporateId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/sensitive-contents/channels";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ChannelIdsList(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getAllSummarySensitiveContents(props: { corporateId: number }): Promise<ApiResult<schema.SensitiveContentListViewList>> {
  let path = "/gaapi/corporations/{corporateId}/sensitive-contents/summaries";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.SensitiveContentListViewList> => {
    return mapper.deserialize_SensitiveContentListViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveSummarySensitiveContents(props: { body: schema.SummaryIdsList; corporateId: number }): Promise<ApiResult> {
  let path = "/gaapi/corporations/{corporateId}/sensitive-contents/summaries";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_SummaryIdsList(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getAllSensitiveTags(props: { corporateId: number }): Promise<ApiResult<schema.CountryRestrictedTagArray>> {
  let path = "/gaapi/corporations/{corporateId}/sensitive-tags";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CountryRestrictedTagArray> => {
    return mapper.deserialize_CountryRestrictedTagArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveSensitiveTags(props: { body: schema.CountryRestrictedTagArray; corporateId: number }): Promise<ApiResult<schema.CountryRestrictedTagArray>> {
  let path = "/gaapi/corporations/{corporateId}/sensitive-tags";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "PUT",
    requestBody: ((value) => {
      return mapper.serialize_CountryRestrictedTagArray(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CountryRestrictedTagArray> => {
    return mapper.deserialize_CountryRestrictedTagArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getEmbeddedQuicksightDashboardURI(props: { corporateId: number }): Promise<ApiResult<string>> {
  let path = "/gaapi/corporations/{corporateId}/statistics/dashboard";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<string> => {
    const data = parseJsonWithNumbersAsBigint(value);
    return ((value) => {
      if (typeof value !== "string") {
        return {
          success: false,
          reason: `[class StringSchema {
    class Schema {
        type: string
        format: null
        $ref: null
        description: null
        title: null
        multipleOf: null
        maximum: null
        exclusiveMaximum: null
        minimum: null
        exclusiveMinimum: null
        maxLength: null
        minLength: null
        pattern: null
        maxItems: null
        minItems: null
        uniqueItems: null
        maxProperties: null
        minProperties: null
        required: null
        not: null
        properties: null
        additionalProperties: null
        nullable: null
        readOnly: null
        writeOnly: null
        example: null
        externalDocs: null
        deprecated: null
        discriminator: null
        xml: null
    }
}]: '${value}' expected to be of type string.`,
        };
      }
      return { success: true, value: value };
    })(data);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getEmbeddedHistoricalQuicksightDashboardURI(props: { corporateId: number }): Promise<ApiResult<string>> {
  let path = "/gaapi/corporations/{corporateId}/statistics/historical-dashboard";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<string> => {
    const data = parseJsonWithNumbersAsBigint(value);
    return ((value) => {
      if (typeof value !== "string") {
        return {
          success: false,
          reason: `[class StringSchema {
    class Schema {
        type: string
        format: null
        $ref: null
        description: null
        title: null
        multipleOf: null
        maximum: null
        exclusiveMaximum: null
        minimum: null
        exclusiveMinimum: null
        maxLength: null
        minLength: null
        pattern: null
        maxItems: null
        minItems: null
        uniqueItems: null
        maxProperties: null
        minProperties: null
        required: null
        not: null
        properties: null
        additionalProperties: null
        nullable: true
        readOnly: null
        writeOnly: null
        example: null
        externalDocs: null
        deprecated: null
        discriminator: null
        xml: null
    }
}]: '${value}' expected to be of type string.`,
        };
      }
      return { success: true, value: value };
    })(data);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function testWelcomeEmailTemplate(props: { corporateId: number }): Promise<ApiResult<schema.EmailTemplateTestResult>> {
  let path = "/gaapi/corporations/{corporateId}/welcome-email";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.EmailTemplateTestResult> => {
    return mapper.deserialize_EmailTemplateTestResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCustomerComments(props: { customerId: number }): Promise<ApiResult<schema.CustomerCommentForms>> {
  let path = "/gaapi/customer-comments/{customerId}";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerCommentForms> => {
    return mapper.deserialize_CustomerCommentForms(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function createCustomerComment(props: { body: schema.CustomerCommentSaveRequest; customerId: number }): Promise<ApiResult> {
  let path = "/gaapi/customer-comments/{customerId}";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CustomerCommentSaveRequest(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getCustomerDetail(props: { customerId: number }): Promise<ApiResult<schema.CustomerAdminDetailForm>> {
  let path = "/gaapi/customer/{customerId}";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminDetailForm> => {
    return mapper.deserialize_CustomerAdminDetailForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function updateCustomerDetails(props: { body: schema.CustomerAdminDetailForm; customerId: number }): Promise<ApiResult<schema.CustomerAdminDetailForm>> {
  let path = "/gaapi/customer/{customerId}";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({
    path: path,
    method: "PUT",
    requestBody: ((value) => {
      return mapper.serialize_CustomerAdminDetailForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminDetailForm> => {
    return mapper.deserialize_CustomerAdminDetailForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getLeadPortals(props: { customerId: number }): Promise<ApiResult<schema.CustomerAdminB2BPortalsForms>> {
  let path = "/gaapi/customer/{customerId}/LeadPortals";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminB2BPortalsForms> => {
    return mapper.deserialize_CustomerAdminB2BPortalsForms(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCustomerAccountDetails(props: { customerId: number }): Promise<ApiResult<schema.CustomerAdminAccountDetailForm>> {
  let path = "/gaapi/customer/{customerId}/accountdetails";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminAccountDetailForm> => {
    return mapper.deserialize_CustomerAdminAccountDetailForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getB2BPortals(props: { customerId: number }): Promise<ApiResult<schema.CustomerAdminB2BPortalsForms>> {
  let path = "/gaapi/customer/{customerId}/b2bportals";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminB2BPortalsForms> => {
    return mapper.deserialize_CustomerAdminB2BPortalsForms(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function deactivateCorporateCustomer(props: { customerId: number; corporateId: number; portalType: schema.PortalType }): Promise<ApiResult<schema.CustomerAdminB2BPortalsForms>> {
  let path = "/gaapi/customer/{customerId}/b2bportals/{corporateId}/deactivate/{portalType}";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{portalType}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return mapper.serialize_PortalType(value);
        })(),
      );
    })(props.portalType),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminB2BPortalsForms> => {
    return mapper.deserialize_CustomerAdminB2BPortalsForms(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getFreeAbstractCoupons(props: { customerId: number }): Promise<ApiResult<schema.CustomerAdminFreeAbstractCoupons>> {
  let path = "/gaapi/customer/{customerId}/freeAbstractCoupons";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminFreeAbstractCoupons> => {
    return mapper.deserialize_CustomerAdminFreeAbstractCoupons(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getFreeDownloads(props: { customerId: number }): Promise<ApiResult<schema.CustomerAdminB2BPortalsForms>> {
  let path = "/gaapi/customer/{customerId}/freedownloads";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminB2BPortalsForms> => {
    return mapper.deserialize_CustomerAdminB2BPortalsForms(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getMobileAppSettings(props: { customerId: number }): Promise<ApiResult<schema.CustomerAdminMobileAppForm>> {
  let path = "/gaapi/customer/{customerId}/mobile";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminMobileAppForm> => {
    return mapper.deserialize_CustomerAdminMobileAppForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function setMobileAppLoginPreference(props: { body: schema.CustomerAdminMobileAppForm; customerId: number }): Promise<ApiResult<schema.CustomerAdminMobileAppForm>> {
  let path = "/gaapi/customer/{customerId}/mobile/loginPreference";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CustomerAdminMobileAppForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminMobileAppForm> => {
    return mapper.deserialize_CustomerAdminMobileAppForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function revokeMobileAppToken(props: { customerId: number; token: string }): Promise<ApiResult<schema.CustomerAdminMobileAppForm>> {
  let path = "/gaapi/customer/{customerId}/mobile/token/{token}";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  path = path.replace(
    "{token}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.token),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminMobileAppForm> => {
    return mapper.deserialize_CustomerAdminMobileAppForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function resetLoginThrottler(props: { customerId: number }): Promise<ApiResult<schema.CustomerAdminAccountDetailForm>> {
  let path = "/gaapi/customer/{customerId}/resetLoginThrottler";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminAccountDetailForm> => {
    return mapper.deserialize_CustomerAdminAccountDetailForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getB2CSubscriptions(props: { customerId: number }): Promise<ApiResult<schema.CustomerAdminSubscriptionsForm>> {
  let path = "/gaapi/customer/{customerId}/subscriptions";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminSubscriptionsForm> => {
    return mapper.deserialize_CustomerAdminSubscriptionsForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function quickSearchCustomers(props: { body: schema.CustomerSearchForm }): Promise<ApiResult<schema.CustomerAdminQuickSearchResult>> {
  const path = "/gaapi/customers/admin-quicksearch";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CustomerSearchForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminQuickSearchResult> => {
    return mapper.deserialize_CustomerAdminQuickSearchResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function searchCustomers(props: { body: schema.CustomerSearchForm }): Promise<ApiResult<schema.CustomerAdminListViewArray>> {
  const path = "/gaapi/customers/admin-search";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CustomerSearchForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.CustomerAdminListViewArray> => {
    return mapper.deserialize_CustomerAdminListViewArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateFormCheckbox(props: { body: schema.DemoFormCheckbox }): Promise<ApiResult<schema.DemoFormCheckbox>> {
  const path = "/gaapi/demo-page/form/checkbox";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_DemoFormCheckbox(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DemoFormCheckbox> => {
    return mapper.deserialize_DemoFormCheckbox(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateFormDate(props: { body: schema.DemoFormDate }): Promise<ApiResult<schema.DemoFormDate>> {
  const path = "/gaapi/demo-page/form/date";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_DemoFormDate(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DemoFormDate> => {
    return mapper.deserialize_DemoFormDate(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateFormFile(props: { body: schema.DemoFormFile }): Promise<ApiResult<schema.DemoFormFile>> {
  const path = "/gaapi/demo-page/form/file";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_DemoFormFile(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DemoFormFile> => {
    return mapper.deserialize_DemoFormFile(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateFormNumber(props: { body: schema.DemoFormNumber }): Promise<ApiResult<schema.DemoFormNumber>> {
  const path = "/gaapi/demo-page/form/number";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_DemoFormNumber(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DemoFormNumber> => {
    return mapper.deserialize_DemoFormNumber(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateFormPassword(props: { body: schema.DemoFormPassword }): Promise<ApiResult<schema.DemoFormPassword>> {
  const path = "/gaapi/demo-page/form/password";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_DemoFormPassword(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DemoFormPassword> => {
    return mapper.deserialize_DemoFormPassword(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateFormRadio(props: { body: schema.DemoFormRadio }): Promise<ApiResult<schema.DemoFormRadio>> {
  const path = "/gaapi/demo-page/form/radio";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_DemoFormRadio(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DemoFormRadio> => {
    return mapper.deserialize_DemoFormRadio(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateFormSelect(props: { body: schema.DemoFormSelect }): Promise<ApiResult<schema.DemoFormSelect>> {
  const path = "/gaapi/demo-page/form/select";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_DemoFormSelect(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DemoFormSelect> => {
    return mapper.deserialize_DemoFormSelect(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateFormText(props: { body: schema.DemoFormText }): Promise<ApiResult<schema.DemoFormText>> {
  const path = "/gaapi/demo-page/form/text";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_DemoFormText(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DemoFormText> => {
    return mapper.deserialize_DemoFormText(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateFormTextarea(props: { body: schema.DemoFormTextarea }): Promise<ApiResult<schema.DemoFormTextarea>> {
  const path = "/gaapi/demo-page/form/textarea";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_DemoFormTextarea(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DemoFormTextarea> => {
    return mapper.deserialize_DemoFormTextarea(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getTemplates(props: { categoryId: number }): Promise<ApiResult<schema.EmailTemplateCategoryInfo>> {
  let path = "/gaapi/emailtool/categories/{categoryId}/templates";
  path = path.replace(
    "{categoryId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.categoryId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.EmailTemplateCategoryInfo> => {
    return mapper.deserialize_EmailTemplateCategoryInfo(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveTemplate(props: { body: schema.EmailTemplateForm; categoryId: number; templateId: number }): Promise<ApiResult<schema.EmailTemplateEditView>> {
  let path = "/gaapi/emailtool/categories/{categoryId}/templates/{templateId}";
  path = path.replace(
    "{categoryId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.categoryId),
  );
  path = path.replace(
    "{templateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.templateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_EmailTemplateForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.EmailTemplateEditView> => {
    return mapper.deserialize_EmailTemplateEditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function deleteTemplate(props: { categoryId: number; templateId: number }): Promise<ApiResult> {
  let path = "/gaapi/emailtool/categories/{categoryId}/templates/{templateId}";
  path = path.replace(
    "{categoryId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.categoryId),
  );
  path = path.replace(
    "{templateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.templateId),
  );
  const response = await gaApiCall({ path: path, method: "DELETE", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function testTemplate(props: { body: schema.EmailTemplateTestForm; categoryId: number; templateId: number }): Promise<ApiResult<schema.EmailTemplateTestResult>> {
  let path = "/gaapi/emailtool/categories/{categoryId}/templates/{templateId}/test";
  path = path.replace(
    "{categoryId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.categoryId),
  );
  path = path.replace(
    "{templateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.templateId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_EmailTemplateTestForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.EmailTemplateTestResult> => {
    return mapper.deserialize_EmailTemplateTestResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getEmailPools(): Promise<ApiResult<schema.EmailPoolAdminListView>> {
  const path = "/gaapi/emailtool/pools";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.EmailPoolAdminListView> => {
    return mapper.deserialize_EmailPoolAdminListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function updateEmailPool(props: { body: schema.EmailPoolForm; poolName: string }): Promise<ApiResult<schema.EmailPoolAdminUpdateResult>> {
  let path = "/gaapi/emailtool/pools/{poolName}";
  path = path.replace(
    "{poolName}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.poolName),
  );
  const response = await gaApiCall({
    path: path,
    method: "PUT",
    requestBody: ((value) => {
      return mapper.serialize_EmailPoolForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.EmailPoolAdminUpdateResult> => {
    return mapper.deserialize_EmailPoolAdminUpdateResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function createEmailPool(props: { body: schema.EmailPoolForm; poolName: string }): Promise<ApiResult<schema.EmailPoolAdminCreateResult>> {
  let path = "/gaapi/emailtool/pools/{poolName}";
  path = path.replace(
    "{poolName}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.poolName),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_EmailPoolForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.EmailPoolAdminCreateResult> => {
    return mapper.deserialize_EmailPoolAdminCreateResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getEmailRepositories(): Promise<ApiResult<schema.EmailRepositoryAdminListView>> {
  const path = "/gaapi/emailtool/repositories";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.EmailRepositoryAdminListView> => {
    return mapper.deserialize_EmailRepositoryAdminListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getEmailRepositoryById(props: { repositoryId: number }): Promise<ApiResult<schema.EmailRepositoryAdminView>> {
  let path = "/gaapi/emailtool/repositories/{repositoryId}";
  path = path.replace(
    "{repositoryId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.repositoryId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.EmailRepositoryAdminView> => {
    return mapper.deserialize_EmailRepositoryAdminView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getExpertVideos(): Promise<ApiResult<schema.ExpertVideoAdminOverview>> {
  const path = "/gaapi/expertvideos/admin";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ExpertVideoAdminOverview> => {
    return mapper.deserialize_ExpertVideoAdminOverview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveExpertVideoAdminForm(props: { body: schema.ExpertVideoAdminForm; expertVideoId: bigint }): Promise<ApiResult<schema.ExpertVideoSaveResponse>> {
  let path = "/gaapi/expertvideos/{expertVideoId}/admin";
  path = path.replace(
    "{expertVideoId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.expertVideoId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ExpertVideoAdminForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ExpertVideoSaveResponse> => {
    return mapper.deserialize_ExpertVideoSaveResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getRecordedExpertVideos(props: { body: schema.ExpertVideoPagingInfo }): Promise<ApiResult<schema.ExpertVideosResult>> {
  const path = "/gaapi/expertvideos/getRecordedExpertVideos";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ExpertVideoPagingInfo(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ExpertVideosResult> => {
    return mapper.deserialize_ExpertVideosResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getTaggedItems(props: { expertVideoId: bigint }): Promise<ApiResult<schema.ExpertVideoTaggedItems>> {
  let path = "/gaapi/expertvideos/getTaggedItems";
  const urlSearchParams = new URLSearchParams();
  [props.expertVideoId].flat().forEach((value) => {
    urlSearchParams.append(
      "expertVideoId",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ExpertVideoTaggedItems> => {
    return mapper.deserialize_ExpertVideoTaggedItems(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function createExport(): Promise<ApiResult<schema.BytesData>> {
  const path = "/gaapi/explore/export";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.BytesData> => {
    return mapper.deserialize_BytesData(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getFeatures(): Promise<ApiResult<schema.FeatureTypeArr>> {
  const path = "/gaapi/features";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.FeatureTypeArr> => {
    return mapper.deserialize_FeatureTypeArr(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function submitLeadForm(props: { body: schema.LeadForm }): Promise<ApiResult<schema.LeadCreationResponse>> {
  const path = "/gaapi/lead-form";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_LeadForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.LeadCreationResponse> => {
    return mapper.deserialize_LeadCreationResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function applyAITag(props: { body: schema.ReferencedLearningItem }): Promise<ApiResult> {
  const path = "/gaapi/learning-items/applyAiTags";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ReferencedLearningItem(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function applyProxmityScore(props: { body: schema.ReferencedLearningItem }): Promise<ApiResult> {
  const path = "/gaapi/learning-items/applyProximityScore";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ReferencedLearningItem(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function generateMiab(props: { dataId: number }): Promise<ApiResult<schema.GenerateMiabAdminResponse>> {
  let path = "/gaapi/miab/admin";
  const urlSearchParams = new URLSearchParams();
  [props.dataId].flat().forEach((value) => {
    urlSearchParams.append(
      "dataId",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.GenerateMiabAdminResponse> => {
    return mapper.deserialize_GenerateMiabAdminResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getAiSuggestion(props: { body: schema.MiabAiSuggestionRequest }): Promise<ApiResult<schema.MiabAiSuggestionResponse>> {
  const path = "/gaapi/miab/aiSuggestion";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_MiabAiSuggestionRequest(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.MiabAiSuggestionResponse> => {
    return mapper.deserialize_MiabAiSuggestionResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function deleteMiab(props: { miabId: bigint }): Promise<ApiResult> {
  let path = "/gaapi/miab/{miabId}/delete";
  path = path.replace(
    "{miabId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.miabId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getPopularSummaries(props: { selectedChannel: bigint; pageNative: number; pageEnglish: number }): Promise<ApiResult<schema.MIABPopularSummariesResult>> {
  let path = "/gaapi/miab/getPopularSummaries";
  const urlSearchParams = new URLSearchParams();
  [props.selectedChannel].flat().forEach((value) => {
    urlSearchParams.append(
      "selectedChannel",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  [props.pageNative].flat().forEach((value) => {
    urlSearchParams.append(
      "pageNative",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  [props.pageEnglish].flat().forEach((value) => {
    urlSearchParams.append(
      "pageEnglish",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.MIABPopularSummariesResult> => {
    return mapper.deserialize_MIABPopularSummariesResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function giveFeedback(props: { body: schema.MIABAnalyticsFeedbackEventForm }): Promise<ApiResult> {
  const path = "/gaapi/miab/giveFeedback";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_MIABAnalyticsFeedbackEventForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function saveCustomQuestion(props: { body: schema.MIABCustomQuestionsForm }): Promise<ApiResult<schema.MIABCustomQuestionsResponse>> {
  const path = "/gaapi/miab/saveCustomQuestions";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_MIABCustomQuestionsForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.MIABCustomQuestionsResponse> => {
    return mapper.deserialize_MIABCustomQuestionsResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function bookmarkMiab(props: { miabId: bigint }): Promise<ApiResult<schema.BookmarkResponse>> {
  let path = "/gaapi/miab/{miabId}/bookmark";
  path = path.replace(
    "{miabId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.miabId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.BookmarkResponse> => {
    return mapper.deserialize_BookmarkResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getNewestSummaries(props: { corporateId: number; daysBack: number; pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedSummaryListView>> {
  let path = "/gaapi/portal-admin-overview/{corporateId}/summaries";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const urlSearchParams = new URLSearchParams();
  [props.daysBack].flat().forEach((value) => {
    urlSearchParams.append(
      "daysBack",
      ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedSummaryListView> => {
    return mapper.deserialize_PagedSummaryListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getEditorsWriters(props: { body: schema.FreelancerContactFilterForm }): Promise<ApiResult<schema.FreelancerContactWriterListView>> {
  const path = "/gaapi/pps/freelancers/contact";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_FreelancerContactFilterForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.FreelancerContactWriterListView> => {
    return mapper.deserialize_FreelancerContactWriterListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function sendEmail(props: { body: schema.FreelancerContactForm }): Promise<ApiResult> {
  const path = "/gaapi/pps/freelancers/contact/send-mail";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_FreelancerContactForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getEditorsFilters(props: { customerId: number }): Promise<ApiResult<schema.FreelancerContactFilterForm>> {
  let path = "/gaapi/pps/{customerId}/freelancer-filters";
  path = path.replace(
    "{customerId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.customerId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.FreelancerContactFilterForm> => {
    return mapper.deserialize_FreelancerContactFilterForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getHyphenationExceptions(props: { isoCode: schema.Language }): Promise<ApiResult<schema.HyphenationExceptions>> {
  let path = "/gaapi/pps/hyphenation-exceptions/{isoCode}";
  path = path.replace(
    "{isoCode}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return mapper.serialize_Language(value);
        })(),
      );
    })(props.isoCode),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.HyphenationExceptions> => {
    return mapper.deserialize_HyphenationExceptions(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getUnpaidInvoices(): Promise<ApiResult<schema.InvoicePaymentCardViewList>> {
  const path = "/gaapi/pps/invoices";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.InvoicePaymentCardViewList> => {
    return mapper.deserialize_InvoicePaymentCardViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function pay(props: { body: schema.InvoicePaymentParameters }): Promise<ApiResult> {
  const path = "/gaapi/pps/invoices";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_InvoicePaymentParameters(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getLanguages(): Promise<ApiResult<schema.DropdownEntryList>> {
  const path = "/gaapi/pps/select/languages";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DropdownEntryList> => {
    return mapper.deserialize_DropdownEntryList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getProducts(): Promise<ApiResult<schema.ProductAdminViewList>> {
  const path = "/gaapi/products";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ProductAdminViewList> => {
    return mapper.deserialize_ProductAdminViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVisibleProducts(): Promise<ApiResult<schema.ProductAdminViewList>> {
  const path = "/gaapi/visible-products";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ProductAdminViewList> => {
    return mapper.deserialize_ProductAdminViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getUserContext(): Promise<ApiResult<schema.UserContext>> {
  const path = "/gaapi/prospect/context";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.UserContext> => {
    return mapper.deserialize_UserContext(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCountries(props: { isoCode: schema.Language }): Promise<ApiResult<schema.Countries>> {
  let path = "/gaapi/prospect/countries";
  const urlSearchParams = new URLSearchParams();
  [props.isoCode].flat().forEach((value) => {
    urlSearchParams.append(
      "isoCode",
      ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return mapper.serialize_Language(value);
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.Countries> => {
    return mapper.deserialize_Countries(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getUserCountry(): Promise<ApiResult<schema.Country>> {
  const path = "/gaapi/prospect/country";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.Country> => {
    return mapper.deserialize_Country(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getMiabs(): Promise<ApiResult<schema.MIABContents>> {
  const path = "/gaapi/prospect/miab";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.MIABContents> => {
    return mapper.deserialize_MIABContents(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getMiab(props: { miabId: bigint }): Promise<ApiResult<schema.ContentMetadata_MIAB>> {
  let path = "/gaapi/prospect/miab/{miabId}";
  path = path.replace(
    "{miabId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.miabId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ContentMetadata_MIAB> => {
    return mapper.deserialize_ContentMetadata_MIAB(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getSummary(props: { dataId: number }): Promise<ApiResult<schema.ContentMetadata_SUMMARY>> {
  let path = "/gaapi/prospect/summary/{dataId}";
  path = path.replace(
    "{dataId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.dataId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ContentMetadata_SUMMARY> => {
    return mapper.deserialize_ContentMetadata_SUMMARY(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveRegistrationProcess(props: { body: schema.RegistrationProcessFormAndSteps }): Promise<ApiResult<schema.RegistrationValidationResult>> {
  const path = "/gaapi/registration/process";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_RegistrationProcessFormAndSteps(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.RegistrationValidationResult> => {
    return mapper.deserialize_RegistrationValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getRevenueReportOverview(props: { revenueReportSearchForm: schema.RevenueReportSearchForm }): Promise<ApiResult<schema.RevenueViewList>> {
  let path = "/gaapi/revenuereport/admin";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_RevenueReportSearchForm(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.revenueReportSearchForm);
    assert(hasProp(outerParsed, "isCompanyBilling"));
    [outerParsed.isCompanyBilling].flat().forEach((value) => {
      urlSearchParams.append(
        "isCompanyBilling",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "isEUCountries"));
    [outerParsed.isEUCountries].flat().forEach((value) => {
      urlSearchParams.append(
        "isEUCountries",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "quarter"));
    [outerParsed.quarter].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "quarter",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "year"));
    [outerParsed.year].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "year",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.RevenueViewList> => {
    return mapper.deserialize_RevenueViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getRightsManagersDropdown(): Promise<ApiResult<schema.DropdownEntryList>> {
  const path = "/gaapi/rights-manager/selection";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DropdownEntryList> => {
    return mapper.deserialize_DropdownEntryList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function uploadPublicKeyCertificate(props: { body: schema.CertificatePublicKeyForm }): Promise<ApiResult<schema.AspCertificateView>> {
  const path = "/gaapi/sp/certificate-public-key";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_CertificatePublicKeyForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.AspCertificateView> => {
    return mapper.deserialize_AspCertificateView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCertificates(): Promise<ApiResult<schema.AspCertificateListViewList>> {
  const path = "/gaapi/sp/certificates";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.AspCertificateListViewList> => {
    return mapper.deserialize_AspCertificateListViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getReports(props: { corporateId: number }): Promise<ApiResult<schema.PagedReportListView>> {
  let path = "/gaapi/stats/b2b/{corporateId}/reports";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedReportListView> => {
    return mapper.deserialize_PagedReportListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getExportedReports(props: { corporateId: number }): Promise<ApiResult<schema.PagedExportReportListView>> {
  let path = "/gaapi/stats/b2b/{corporateId}/reports/exported";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedExportReportListView> => {
    return mapper.deserialize_PagedExportReportListView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function initB2bReport(props: { corporateId: number }): Promise<ApiResult<schema.B2BStatsReportPageProps>> {
  let path = "/gaapi/stats/b2b/{corporateId}/reports/init";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.B2BStatsReportPageProps> => {
    return mapper.deserialize_B2BStatsReportPageProps(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getReport(props: { corporateId: number; reportId: number }): Promise<ApiResult<schema.NewB2BReportForm>> {
  let path = "/gaapi/stats/b2b/{corporateId}/reports/{reportId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{reportId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.reportId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.NewB2BReportForm> => {
    return mapper.deserialize_NewB2BReportForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveReport(props: { body: schema.NewB2BReportForm; corporateId: number; reportId: number }): Promise<ApiResult<schema.B2BStatsReportSaveResponse>> {
  let path = "/gaapi/stats/b2b/{corporateId}/reports/{reportId}";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{reportId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.reportId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_NewB2BReportForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.B2BStatsReportSaveResponse> => {
    return mapper.deserialize_B2BStatsReportSaveResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function deleteReport(props: { corporateId: number; reportId: number }): Promise<ApiResult> {
  let path = "/gaapi/stats/b2b/{corporateId}/reports/{reportId}/delete";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{reportId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.reportId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function runReport(props: { corporateId: number; reportId: number }): Promise<ApiResult> {
  let path = "/gaapi/stats/b2b/{corporateId}/reports/{reportId}/run";
  path = path.replace(
    "{corporateId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.corporateId),
  );
  path = path.replace(
    "{reportId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.reportId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function sendConfirmationEmail(props: { body: schema.ResendConfirmationEmailForm; subscriptionId: number }): Promise<ApiResult<schema.ResendConfirmationEmailForm>> {
  let path = "/gaapi/subscriptions/confirmation-email/{subscriptionId}";
  path = path.replace(
    "{subscriptionId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.subscriptionId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_ResendConfirmationEmailForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ResendConfirmationEmailForm> => {
    return mapper.deserialize_ResendConfirmationEmailForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getSubscriptionCredit(props: { subscriptionId: number; transactionTypeId: number }): Promise<ApiResult<schema.SubscriptionCreditView>> {
  let path = "/gaapi/subscriptions/credit/{subscriptionId}/{transactionTypeId}";
  path = path.replace(
    "{subscriptionId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.subscriptionId),
  );
  path = path.replace(
    "{transactionTypeId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.transactionTypeId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.SubscriptionCreditView> => {
    return mapper.deserialize_SubscriptionCreditView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function creditSubscription(props: { body: schema.SubscriptionCreditForm; subscriptionId: number; transactionTypeId: number }): Promise<ApiResult<schema.SubscriptionCreditForm>> {
  let path = "/gaapi/subscriptions/credit/{subscriptionId}/{transactionTypeId}";
  path = path.replace(
    "{subscriptionId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.subscriptionId),
  );
  path = path.replace(
    "{transactionTypeId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.transactionTypeId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_SubscriptionCreditForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.SubscriptionCreditForm> => {
    return mapper.deserialize_SubscriptionCreditForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function renewSubscription(props: { body: schema.RenewSubscriptionForm; subscriptionId: number }): Promise<ApiResult<schema.RenewSubscriptionForm>> {
  let path = "/gaapi/subscriptions/renew/{subscriptionId}";
  path = path.replace(
    "{subscriptionId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.subscriptionId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_RenewSubscriptionForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.RenewSubscriptionForm> => {
    return mapper.deserialize_RenewSubscriptionForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function deleteRenewal(props: { subscriptionId: number }): Promise<ApiResult> {
  let path = "/gaapi/subscriptions/renew/{subscriptionId}";
  path = path.replace(
    "{subscriptionId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.subscriptionId),
  );
  const response = await gaApiCall({ path: path, method: "DELETE", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getSubscription(props: { subscriptionId: number }): Promise<ApiResult<schema.SubscriptionAdminView>> {
  let path = "/gaapi/subscriptions/{subscriptionId}";
  path = path.replace(
    "{subscriptionId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.subscriptionId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.SubscriptionAdminView> => {
    return mapper.deserialize_SubscriptionAdminView(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function updateSubscription(props: { body: schema.SubscriptionAdminForm; subscriptionId: number }): Promise<ApiResult<schema.SubscriptionAdminForm>> {
  let path = "/gaapi/subscriptions/{subscriptionId}";
  path = path.replace(
    "{subscriptionId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.subscriptionId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_SubscriptionAdminForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.SubscriptionAdminForm> => {
    return mapper.deserialize_SubscriptionAdminForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function deleteSubscription(props: { subscriptionId: number }): Promise<ApiResult> {
  let path = "/gaapi/subscriptions/{subscriptionId}";
  path = path.replace(
    "{subscriptionId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.subscriptionId),
  );
  const response = await gaApiCall({ path: path, method: "DELETE", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getBulkLoadSummaries(props: { body: schema.SummaryBulkLoadForm }): Promise<ApiResult<schema.SummaryMetadataArray>> {
  const path = "/gaapi/summaries-bulk-load/admin";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_SummaryBulkLoadForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.SummaryMetadataArray> => {
    return mapper.deserialize_SummaryMetadataArray(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function toggleSummaryBookmark(props: { dataId: number }): Promise<ApiResult<schema.BookmarkResponse>> {
  let path = "/gaapi/summary-ix/{dataId}/bookmark";
  path = path.replace(
    "{dataId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.dataId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.BookmarkResponse> => {
    return mapper.deserialize_BookmarkResponse(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getFrontendTranslations(props: { frontendProject: schema.FrontendProject; isoCode: schema.Language }): Promise<ApiResult<schema.Translations>> {
  let path = "/gaapi/txt/txids/{frontendProject}/{isoCode}";
  path = path.replace(
    "{frontendProject}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return mapper.serialize_FrontendProject(value);
        })(),
      );
    })(props.frontendProject),
  );
  path = path.replace(
    "{isoCode}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return mapper.serialize_Language(value);
        })(),
      );
    })(props.isoCode),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.Translations> => {
    return mapper.deserialize_Translations(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupEditForm(props: { body: schema.WarmupEditForm }): Promise<ApiResult<schema.WarmupValidationResult>> {
  const path = "/gaapi/validation/warmup";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEditForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupValidationResult> => {
    return mapper.deserialize_WarmupValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupB2bEditForm(props: { body: schema.WarmupEitherEditForm_B2B }): Promise<ApiResult<schema.WarmupB2bValidationResult>> {
  const path = "/gaapi/validation/warmupb2b";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEitherEditForm_B2B(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupB2bValidationResult> => {
    return mapper.deserialize_WarmupB2bValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupB2cEditForm(props: { body: schema.WarmupEitherEditForm_B2C }): Promise<ApiResult<schema.WarmupB2cValidationResult>> {
  const path = "/gaapi/validation/warmupb2c";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEitherEditForm_B2C(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupB2cValidationResult> => {
    return mapper.deserialize_WarmupB2cValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupEitherEditForm(props: { body: schema.WarmupEitherEditForm }): Promise<ApiResult<schema.WarmupEitherValidationResult>> {
  const path = "/gaapi/validation/warmupeither";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEitherEditForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupEitherValidationResult> => {
    return mapper.deserialize_WarmupEitherValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupSsoEditForm(props: { body: schema.WarmupEitherEditForm_SSO }): Promise<ApiResult<schema.WarmupSsoValidationResult>> {
  const path = "/gaapi/validation/warmupsso";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEitherEditForm_SSO(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupSsoValidationResult> => {
    return mapper.deserialize_WarmupSsoValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupTrialEditForm(props: { body: schema.WarmupEitherEditForm_TRIAL }): Promise<ApiResult<schema.WarmupTrialValidationResult>> {
  const path = "/gaapi/validation/warmuptrial";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEitherEditForm_TRIAL(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupTrialValidationResult> => {
    return mapper.deserialize_WarmupTrialValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function createVerifaiContentForm(props: { body: schema.VerifaiContentForm }): Promise<ApiResult<schema.VerifaiContentFormResult>> {
  const path = "/gaapi/verifai/content";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_VerifaiContentForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiContentFormResult> => {
    return mapper.deserialize_VerifaiContentFormResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getAutofillPromptTemplate(): Promise<ApiResult<string>> {
  const path = "/gaapi/verifai/content/autofill-prompt-template";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<string> => {
    const data = parseJsonWithNumbersAsBigint(value);
    return ((value) => {
      if (typeof value !== "string") {
        return {
          success: false,
          reason: `[class StringSchema {
    class Schema {
        type: string
        format: null
        $ref: null
        description: null
        title: null
        multipleOf: null
        maximum: null
        exclusiveMaximum: null
        minimum: null
        exclusiveMinimum: null
        maxLength: null
        minLength: null
        pattern: null
        maxItems: null
        minItems: null
        uniqueItems: null
        maxProperties: null
        minProperties: null
        required: null
        not: null
        properties: null
        additionalProperties: null
        nullable: null
        readOnly: null
        writeOnly: null
        example: null
        externalDocs: null
        deprecated: null
        discriminator: null
        xml: null
    }
}]: '${value}' expected to be of type string.`,
        };
      }
      return { success: true, value: value };
    })(data);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiContentForm(props: { verifaiContentId: bigint }): Promise<ApiResult<schema.VerifaiContentFormResult>> {
  let path = "/gaapi/verifai/content/{verifaiContentId}";
  path = path.replace(
    "{verifaiContentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiContentId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiContentFormResult> => {
    return mapper.deserialize_VerifaiContentFormResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveVerifaiContentForm(props: { body: schema.VerifaiContentForm; verifaiContentId: bigint }): Promise<ApiResult<schema.VerifaiContentFormResult>> {
  let path = "/gaapi/verifai/content/{verifaiContentId}";
  path = path.replace(
    "{verifaiContentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiContentId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_VerifaiContentForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiContentFormResult> => {
    return mapper.deserialize_VerifaiContentFormResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function autofillContent(props: { body: schema.VerifaiAutofillRequest; verifaiContentId: bigint }): Promise<ApiResult<schema.VerifaiAutofillResult>> {
  let path = "/gaapi/verifai/content/{verifaiContentId}/autofill";
  path = path.replace(
    "{verifaiContentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiContentId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_VerifaiAutofillRequest(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiAutofillResult> => {
    return mapper.deserialize_VerifaiAutofillResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiCreatorPageView(props: { hashId: string }): Promise<ApiResult<schema.VerifaiCreatorViewResult>> {
  let path = "/gaapi/verifai/creators-page/{hashId}";
  path = path.replace(
    "{hashId}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.hashId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiCreatorViewResult> => {
    return mapper.deserialize_VerifaiCreatorViewResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function claimContentItems(props: { body: schema.VerifaiClaimItemsRequest; hashId: string }): Promise<ApiResult<schema.VerifaiCreatorViewResult>> {
  let path = "/gaapi/verifai/creators-page/{hashId}/claim-items";
  path = path.replace(
    "{hashId}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.hashId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_VerifaiClaimItemsRequest(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiCreatorViewResult> => {
    return mapper.deserialize_VerifaiCreatorViewResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function confirmCreatorIdentity(props: { hashId: string }): Promise<ApiResult<schema.VerifaiActionResult>> {
  let path = "/gaapi/verifai/creators-page/{hashId}/confirm-identity";
  path = path.replace(
    "{hashId}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.hashId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiActionResult> => {
    return mapper.deserialize_VerifaiActionResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function optOutCreator(props: { hashId: string }): Promise<ApiResult<schema.VerifaiActionResult>> {
  let path = "/gaapi/verifai/creators-page/{hashId}/opt-out";
  path = path.replace(
    "{hashId}",
    ((value) => {
      return ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.hashId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiActionResult> => {
    return mapper.deserialize_VerifaiActionResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function filterVerifaiCreators(props: { body: schema.VerifaiCreatorFilters; pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedVerifaiCreatorOverview>> {
  let path = "/gaapi/verifai/creators/filter";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_VerifaiCreatorFilters(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedVerifaiCreatorOverview> => {
    return mapper.deserialize_PagedVerifaiCreatorOverview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function searchVerifaiCreators(props: { pageRequest: schema.PageRequest; verifaiCreatorSearch: schema.VerifaiCreatorSearch }): Promise<ApiResult<schema.PagedVerifaiCreatorOverview>> {
  let path = "/gaapi/verifai/creators/search";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_VerifaiCreatorSearch(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.verifaiCreatorSearch);
    assert(hasProp(outerParsed, "search"));
    [outerParsed.search].flat().forEach((value) => {
      urlSearchParams.append(
        "search",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedVerifaiCreatorOverview> => {
    return mapper.deserialize_PagedVerifaiCreatorOverview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiCreatorForm(props: { verifaiCreatorId: bigint }): Promise<ApiResult<schema.VerifaiCreatorForm>> {
  let path = "/gaapi/verifai/creators/{verifaiCreatorId}";
  path = path.replace(
    "{verifaiCreatorId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiCreatorId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiCreatorForm> => {
    return mapper.deserialize_VerifaiCreatorForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveVerifaiCreatorForm(props: { body: schema.VerifaiCreatorForm; verifaiCreatorId: bigint }): Promise<ApiResult<schema.VerifaiCreatorFormResult>> {
  let path = "/gaapi/verifai/creators/{verifaiCreatorId}";
  path = path.replace(
    "{verifaiCreatorId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiCreatorId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_VerifaiCreatorForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiCreatorFormResult> => {
    return mapper.deserialize_VerifaiCreatorFormResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function markCreatorAsContacted(props: { verifaiCreatorId: bigint }): Promise<ApiResult<schema.VerifaiCreatorOverviewItem>> {
  let path = "/gaapi/verifai/creators/{verifaiCreatorId}/contacted";
  path = path.replace(
    "{verifaiCreatorId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiCreatorId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiCreatorOverviewItem> => {
    return mapper.deserialize_VerifaiCreatorOverviewItem(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCreatorContentItems(props: { verifaiCreatorId: bigint }): Promise<ApiResult<schema.VerifaiOverviewItemList>> {
  let path = "/gaapi/verifai/creators/{verifaiCreatorId}/items";
  path = path.replace(
    "{verifaiCreatorId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiCreatorId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiOverviewItemList> => {
    return mapper.deserialize_VerifaiOverviewItemList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getCurators(): Promise<ApiResult<schema.DropdownEntryList>> {
  const path = "/gaapi/verifai/curators";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DropdownEntryList> => {
    return mapper.deserialize_DropdownEntryList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getEditors(): Promise<ApiResult<schema.DropdownEntryList>> {
  const path = "/gaapi/verifai/editors";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DropdownEntryList> => {
    return mapper.deserialize_DropdownEntryList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getManEditors(): Promise<ApiResult<schema.DropdownEntryList>> {
  const path = "/gaapi/verifai/managing-editors";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DropdownEntryList> => {
    return mapper.deserialize_DropdownEntryList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifiers(): Promise<ApiResult<schema.DropdownEntryList>> {
  const path = "/gaapi/verifai/verifiers";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DropdownEntryList> => {
    return mapper.deserialize_DropdownEntryList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiDashboardAssignedForEditing(props: { pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedVerifaiOverview>> {
  let path = "/gaapi/verifai/dashboard/assigned-edit";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedVerifaiOverview> => {
    return mapper.deserialize_PagedVerifaiOverview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiDashboardAssignedForPublishing(props: { pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedVerifaiOverview>> {
  let path = "/gaapi/verifai/dashboard/assigned-publishing";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedVerifaiOverview> => {
    return mapper.deserialize_PagedVerifaiOverview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiDashboardAssignedForVerification(props: { pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedVerifaiOverview>> {
  let path = "/gaapi/verifai/dashboard/assigned-verification";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedVerifaiOverview> => {
    return mapper.deserialize_PagedVerifaiOverview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiDashboardEditable(props: { pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedVerifaiOverview>> {
  let path = "/gaapi/verifai/dashboard/editable";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedVerifaiOverview> => {
    return mapper.deserialize_PagedVerifaiOverview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function invoiceVerifaiDashboardItem(props: { verifaiContentId: bigint }): Promise<ApiResult<schema.VerifaiOverviewItem>> {
  let path = "/gaapi/verifai/dashboard/invoice/{verifaiContentId}";
  path = path.replace(
    "{verifaiContentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiContentId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiOverviewItem> => {
    return mapper.deserialize_VerifaiOverviewItem(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiDashboardInvoiceable(props: { pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedVerifaiOverview>> {
  let path = "/gaapi/verifai/dashboard/invoiceable";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedVerifaiOverview> => {
    return mapper.deserialize_PagedVerifaiOverview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiDashboardOpen(props: { pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedVerifaiOverview>> {
  let path = "/gaapi/verifai/dashboard/open";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedVerifaiOverview> => {
    return mapper.deserialize_PagedVerifaiOverview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function pickEditableItem(props: { verifaiContentId: bigint }): Promise<ApiResult<schema.VerifaiOverviewItem>> {
  let path = "/gaapi/verifai/dashboard/pick-edit/{verifaiContentId}";
  path = path.replace(
    "{verifaiContentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiContentId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiOverviewItem> => {
    return mapper.deserialize_VerifaiOverviewItem(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function pickPublishableItem(props: { verifaiContentId: bigint }): Promise<ApiResult<schema.VerifaiOverviewItem>> {
  let path = "/gaapi/verifai/dashboard/pick-publish/{verifaiContentId}";
  path = path.replace(
    "{verifaiContentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiContentId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiOverviewItem> => {
    return mapper.deserialize_VerifaiOverviewItem(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function pickVerifyableItem(props: { verifaiContentId: bigint }): Promise<ApiResult<schema.VerifaiOverviewItem>> {
  let path = "/gaapi/verifai/dashboard/pick-verify/{verifaiContentId}";
  path = path.replace(
    "{verifaiContentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiContentId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiOverviewItem> => {
    return mapper.deserialize_VerifaiOverviewItem(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiDashboardPublishable(props: { pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedVerifaiOverview>> {
  let path = "/gaapi/verifai/dashboard/publishable";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedVerifaiOverview> => {
    return mapper.deserialize_PagedVerifaiOverview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function unpickEditableItem(props: { verifaiContentId: bigint }): Promise<ApiResult<schema.VerifaiOverviewItem>> {
  let path = "/gaapi/verifai/dashboard/unpick-edit/{verifaiContentId}";
  path = path.replace(
    "{verifaiContentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiContentId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiOverviewItem> => {
    return mapper.deserialize_VerifaiOverviewItem(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function unpickPublishableItem(props: { verifaiContentId: bigint }): Promise<ApiResult<schema.VerifaiOverviewItem>> {
  let path = "/gaapi/verifai/dashboard/unpick-publish/{verifaiContentId}";
  path = path.replace(
    "{verifaiContentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiContentId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiOverviewItem> => {
    return mapper.deserialize_VerifaiOverviewItem(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function unpickVerifyableItem(props: { verifaiContentId: bigint }): Promise<ApiResult<schema.VerifaiOverviewItem>> {
  let path = "/gaapi/verifai/dashboard/unpick-verify/{verifaiContentId}";
  path = path.replace(
    "{verifaiContentId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiContentId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiOverviewItem> => {
    return mapper.deserialize_VerifaiOverviewItem(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiOverview(props: { body: schema.VerifaiOverviewFilters; pageRequest: schema.PageRequest }): Promise<ApiResult<schema.PagedVerifaiOverview>> {
  let path = "/gaapi/verifai/overview";
  const urlSearchParams = new URLSearchParams();
  (() => {
    const outerParsed = ((value) => {
      const outerRendered = ((value) => {
        return mapper.serialize_PageRequest(value);
      })(value);
      const outerParsed = parseJsonWithNumbersAsBigint(outerRendered);
      assert(outerParsed !== null);
      assert(typeof outerParsed === "object");
      return outerParsed;
    })(props.pageRequest);
    assert(hasProp(outerParsed, "page"));
    [outerParsed.page].flat().forEach((value) => {
      urlSearchParams.append(
        "page",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "psize"));
    [outerParsed.psize].flat().forEach((value) => {
      urlSearchParams.append(
        "psize",
        ((value) => {
          return value;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortBy"));
    [outerParsed.sortBy].flat().forEach((value) => {
      if (value === null) return;
      urlSearchParams.append(
        "sortBy",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "string");
          return parsed_again;
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
    assert(hasProp(outerParsed, "sortDesc"));
    [outerParsed.sortDesc].flat().forEach((value) => {
      urlSearchParams.append(
        "sortDesc",
        ((value) => {
          const parsed_again = parseJsonWithNumbersAsBigint(value);
          assert(typeof parsed_again === "boolean");
          return parsed_again ? "true" : "false";
        })(
          (() => {
            return renderJsonWithNumbersAsBigint(value);
          })(),
        ),
      );
    });
  })();
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_VerifaiOverviewFilters(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.PagedVerifaiOverview> => {
    return mapper.deserialize_PagedVerifaiOverview(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiProfiles(): Promise<ApiResult<schema.DropdownEntryList>> {
  const path = "/gaapi/verifai/profiles";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.DropdownEntryList> => {
    return mapper.deserialize_DropdownEntryList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiSites(): Promise<ApiResult<schema.VerifaiSiteFormList>> {
  const path = "/gaapi/verifai/sites";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiSiteFormList> => {
    return mapper.deserialize_VerifaiSiteFormList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiSiteForm(props: { verifaiSiteId: bigint }): Promise<ApiResult<schema.VerifaiSiteForm>> {
  let path = "/gaapi/verifai/sites/{verifaiSiteId}";
  path = path.replace(
    "{verifaiSiteId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiSiteId),
  );
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiSiteForm> => {
    return mapper.deserialize_VerifaiSiteForm(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function saveVerifaiSiteForm(props: { body: schema.VerifaiSiteForm; verifaiSiteId: bigint }): Promise<ApiResult<schema.VerifaiSiteFormResult>> {
  let path = "/gaapi/verifai/sites/{verifaiSiteId}";
  path = path.replace(
    "{verifaiSiteId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.verifaiSiteId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_VerifaiSiteForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiSiteFormResult> => {
    return mapper.deserialize_VerifaiSiteFormResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function getVerifaiTrustMarkers(): Promise<ApiResult<schema.VerifaiTrustMarkerViewList>> {
  const path = "/gaapi/verifai/trust-markers";
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.VerifaiTrustMarkerViewList> => {
    return mapper.deserialize_VerifaiTrustMarkerViewList(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function processWarmupEditForm(props: { body: schema.WarmupEditForm }): Promise<ApiResult<schema.WarmupValidationResult>> {
  const path = "/gaapi/warmup";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEditForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupValidationResult> => {
    return mapper.deserialize_WarmupValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupEditFormManual(props: { body: schema.WarmupEditForm }): Promise<ApiResult<schema.WarmupValidationResult>> {
  const path = "/gaapi/warmup/validate";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEditForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupValidationResult> => {
    return mapper.deserialize_WarmupValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupB2bEditFormManual(props: { body: schema.WarmupEitherEditForm_B2B }): Promise<ApiResult<schema.WarmupB2bValidationResult>> {
  const path = "/gaapi/warmup/validate/b2b";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEitherEditForm_B2B(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupB2bValidationResult> => {
    return mapper.deserialize_WarmupB2bValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupB2cEditFormManual(props: { body: schema.WarmupEitherEditForm_B2C }): Promise<ApiResult<schema.WarmupB2cValidationResult>> {
  const path = "/gaapi/warmup/validate/b2c";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEitherEditForm_B2C(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupB2cValidationResult> => {
    return mapper.deserialize_WarmupB2cValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupEitherEditFormManual(props: { body: schema.WarmupEitherEditForm }): Promise<ApiResult<schema.WarmupEitherValidationResult>> {
  const path = "/gaapi/warmup/validate/either";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEitherEditForm(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupEitherValidationResult> => {
    return mapper.deserialize_WarmupEitherValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupSsoEditFormManual(props: { body: schema.WarmupEitherEditForm_SSO }): Promise<ApiResult<schema.WarmupSsoValidationResult>> {
  const path = "/gaapi/warmup/validate/sso";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEitherEditForm_SSO(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupSsoValidationResult> => {
    return mapper.deserialize_WarmupSsoValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function validateWarmupTrialEditFormManual(props: { body: schema.WarmupEitherEditForm_TRIAL }): Promise<ApiResult<schema.WarmupTrialValidationResult>> {
  const path = "/gaapi/warmup/validate/trial";
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WarmupEitherEditForm_TRIAL(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.WarmupTrialValidationResult> => {
    return mapper.deserialize_WarmupTrialValidationResult(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}

export async function updateManagingEditor(props: { body: schema.WishlistManagementEditorUpdate; kRecId: number }): Promise<ApiResult> {
  let path = "/gaapi/wishlist-management/{kRecId}/editor";
  path = path.replace(
    "{kRecId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.kRecId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WishlistManagementEditorUpdate(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function updateLanguage(props: { body: schema.WishlistManagementLanguageUpdate; kRecId: number }): Promise<ApiResult> {
  let path = "/gaapi/wishlist-management/{kRecId}/language";
  path = path.replace(
    "{kRecId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.kRecId),
  );
  const response = await gaApiCall({
    path: path,
    method: "POST",
    requestBody: ((value) => {
      return mapper.serialize_WishlistManagementLanguageUpdate(value);
    })(props.body),
  });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function markRejected(props: { kRecId: number }): Promise<ApiResult> {
  let path = "/gaapi/wishlist-management/{kRecId}/reject";
  path = path.replace(
    "{kRecId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.kRecId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function markToReview(props: { kRecId: number }): Promise<ApiResult> {
  let path = "/gaapi/wishlist-management/{kRecId}/review";
  path = path.replace(
    "{kRecId}",
    ((value) => {
      return ((value) => {
        return value;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      );
    })(props.kRecId),
  );
  const response = await gaApiCall({ path: path, method: "POST", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  return { kind: "success" };
}

export async function getZipTown(props: { zipCode: string; countryCode: string }): Promise<ApiResult<schema.ZipTown>> {
  let path = "/gaapi/zipTown";
  const urlSearchParams = new URLSearchParams();
  [props.zipCode].flat().forEach((value) => {
    urlSearchParams.append(
      "zipCode",
      ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  [props.countryCode].flat().forEach((value) => {
    urlSearchParams.append(
      "countryCode",
      ((value) => {
        const parsed_again = parseJsonWithNumbersAsBigint(value);
        assert(typeof parsed_again === "string");
        return parsed_again;
      })(
        (() => {
          return renderJsonWithNumbersAsBigint(
            (() => {
              return value;
            })(),
          );
        })(),
      ),
    );
  });
  if (urlSearchParams.size > 0) {
    path += "?";
    path += urlSearchParams.toString();
  }
  const response = await gaApiCall({ path: path, method: "GET", requestBody: null });
  if (response.kind == "error") {
    return response;
  }
  const deserialized = ((value): ParserResult<schema.ZipTown> => {
    return mapper.deserialize_ZipTown(value);
  })(response.data);
  if (deserialized.success == false) {
    console.warn("could not parse response body");
    return { kind: "error", errorCode: null, errorMessage: "deserialize failed" };
  }
  return { kind: "success", data: deserialized.value };
}
